"use client"

import { useEffect, useState } from "react"

export default function ProductVariantSelector({ product, setSelectedVariant, initialVariant }) {
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [isInitialMount, setIsInitialMount] = useState(true);

    // Chuẩn hóa chuỗi
    const normalizeString = (str) => (str ? str.trim().toLowerCase() : '');

    // Tạo mảng màu sắc và kích thước không trùng lặp
    const getUniqueColors = () => {
        const colorMap = new Map();
        product.productVariants.forEach((variant) => {
            if (variant.status === true && variant.color) {
                const normalizedColor = normalizeString(variant.color);
                if (!colorMap.has(normalizedColor)) {
                    colorMap.set(normalizedColor, variant.color);
                }
            }
        });
        return Array.from(colorMap.values());
    };

    const getUniqueSizes = () => {
        const sizeMap = new Map();
        product.productVariants.forEach((variant) => {
            if (variant.status === true && variant.size) {
                const normalizedSize = normalizeString(variant.size);
                if (!sizeMap.has(normalizedSize)) {
                    sizeMap.set(normalizedSize, variant.size);
                }
            }
        });
        return Array.from(sizeMap.values());
    };

    const allColors = getUniqueColors();
    const allSizes = getUniqueSizes();

    // Tìm biến thể có giá rẻ nhất
    const getCheapestVariant = () => {
        if (!product.productVariants || product.productVariants.length === 0) return null;
        return product.productVariants
            .filter(variant => variant.status === true)
            .reduce((cheapest, variant) => {
                const finalPrice = variant.price * (1 - (variant.discount || 0) / 100);
                const cheapestPrice = cheapest ? cheapest.price * (1 - (cheapest.discount || 0) / 100) : Infinity;
                return finalPrice < cheapestPrice ? variant : cheapest;
            }, null);
    };

    // Đồng bộ với biến thể rẻ nhất khi component mount lần đầu
    useEffect(() => {
        if (isInitialMount && !selectedColor && !selectedSize) {
            const cheapestVariant = getCheapestVariant();
            if (cheapestVariant) {
                setSelectedColor(cheapestVariant.color);
                setSelectedSize(cheapestVariant.size);
                setSelectedVariant(cheapestVariant);
            }
            setIsInitialMount(false);
        }
    }, [product.productVariants, isInitialMount, selectedColor, selectedSize, setSelectedVariant]);

    // Cập nhật selectedVariant khi color/size thay đổi
    useEffect(() => {
        if (selectedColor && selectedSize) {
            const variant = product.productVariants.find(
                (v) =>
                    normalizeString(v.color) === normalizeString(selectedColor) &&
                    normalizeString(v.size) === normalizeString(selectedSize) &&
                    v.status === true
            );
            setSelectedVariant(variant || null);
        } else {
            setSelectedVariant(null);
        }
    }, [selectedColor, selectedSize, product.productVariants, setSelectedVariant]);

    // Lấy danh sách kích thước khả dụng dựa trên màu đã chọn
    const availableSizes = selectedColor
        ? [...new Set(
            product.productVariants
                .filter((v) => normalizeString(v.color) === normalizeString(selectedColor) && v.status === true)
                .map((v) => v.size)
        )].filter(size => size)
        : allSizes;

    // Lấy danh sách màu sắc khả dụng dựa trên kích thước đã chọn
    const availableColors = selectedSize
        ? [...new Set(
            product.productVariants
                .filter((v) => normalizeString(v.size) === normalizeString(selectedSize) && v.status === true)
                .map((v) => v.color)
        )].filter(color => color)
        : allColors;

    const handleColorSelect = (color) => {
        // Cho phép bỏ chọn nếu nhấn lại màu đã chọn
        if (normalizeString(selectedColor) === normalizeString(color)) {
            setSelectedColor(null);
        } else {
            setSelectedColor(color);
            // Nếu kích thước hiện tại không khả dụng với màu mới, đặt lại kích thước
            if (
                selectedSize &&
                !product.productVariants.some(
                    (v) =>
                        normalizeString(v.color) === normalizeString(color) &&
                        normalizeString(v.size) === normalizeString(selectedSize) &&
                        v.status === true
                )
            ) {
                setSelectedSize(null);
            }
        }
    };

    const handleSizeSelect = (size) => {
        // Cho phép bỏ chọn nếu nhấn lại kích thước đã chọn
        if (normalizeString(selectedSize) === normalizeString(size)) {
            setSelectedSize(null);
        } else {
            setSelectedSize(size);
            // Nếu màu hiện tại không khả dụng với kích thước mới, đặt lại màu
            if (
                selectedColor &&
                !product.productVariants.some(
                    (v) =>
                        normalizeString(v.size) === normalizeString(size) &&
                        normalizeString(v.color) === normalizeString(selectedColor) &&
                        v.status === true
                )
            ) {
                setSelectedColor(null);
            }
        }
    };

    const isVariantAvailable = (color, size) => {
        return product.productVariants.some(
            (v) =>
                normalizeString(v.color) === normalizeString(color) &&
                normalizeString(v.size) === normalizeString(size) &&
                v.status === true
        );
    };

    if (allColors.length === 0 && allSizes.length === 0) {
        return <div className="mt-3 text-red-500 text-sm">Không có biến thể sản phẩm nào khả dụng.</div>;
    }

    return (
        <div className="pt-3 rounded-lg mb-4">
            <div className="mb-4">
                <p className="font-semibold mb-2">Màu sắc:</p>
                <div className="flex flex-wrap gap-2">
                    {allColors.map((color) => {
                        const isAvailable = selectedSize
                            ? availableColors.includes(color)
                            : true;
                        return (
                            <button
                                key={color}
                                className={`px-3 py-1 border rounded transition-all ${
                                    selectedColor && normalizeString(selectedColor) === normalizeString(color)
                                        ? "bg-blue-500 text-white"
                                        : isAvailable
                                            ? "bg-gray-200 hover:bg-gray-300"
                                            : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                                }`}
                                onClick={() => isAvailable && handleColorSelect(color)}
                                disabled={!isAvailable}
                                title={
                                    selectedColor && normalizeString(selectedColor) === normalizeString(color)
                                        ? "Nhấp để bỏ chọn"
                                        : ""
                                }
                            >
                                {color}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <p className="font-semibold mb-2">Kích thước:</p>
                <div className="flex flex-wrap gap-2">
                    {allSizes.map((size) => {
                        const isAvailable = selectedColor
                            ? availableSizes.includes(size)
                            : true;
                        return (
                            <button
                                key={size}
                                className={`px-3 py-1 border rounded transition-all ${
                                    selectedSize && normalizeString(selectedSize) === normalizeString(size)
                                        ? "bg-blue-500 text-white"
                                        : isAvailable
                                            ? "bg-gray-200 hover:bg-gray-300"
                                            : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                                }`}
                                onClick={() => isAvailable && handleSizeSelect(size)}
                                disabled={!isAvailable}
                                title={
                                    selectedSize && normalizeString(selectedSize) === normalizeString(size)
                                        ? "Nhấp để bỏ chọn"
                                        : ""
                                }
                            >
                                {size}
                            </button>
                        );
                    })}
                </div>
            </div>

            {selectedColor && selectedSize && !isVariantAvailable(selectedColor, selectedSize) && (
                <div className="mt-3 text-red-500 text-sm">Biến thể sản phẩm này không có sẵn.</div>
            )}
        </div>
    );
}